// Tab API Keys — estratto verbatim da DeveloperSettings.tsx (split Task free-win).
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Trash2, Key, Shield, Eye, EyeOff, AlertTriangle, CheckCircle2, Download, FileText, AlertCircle, Server, Globe, Send, RefreshCw, Webhook, BookOpen, Code, Wrench, Puzzle, Lock, Pencil, Search, X, ChevronUp, ChevronDown, type LucideIcon } from 'lucide-react';
import {
  ALL_AVAILABLE_SCOPES,
  SCOPE_CATEGORIES,
  SCOPE_TO_EVENTS_MAP,
  WEBHOOK_EVENT_CATEGORIES as SHARED_EVENT_CATEGORIES,
  getAllowedEventsForScopes,
} from '@easyflow/shared';
import { format } from 'date-fns';
import { HubShell } from '@/components/common/HubShell';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient as api, getPublicBaseUrl } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { useSectionNavState } from '@/hooks/useSectionNavState';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import MCPToolsTab from '@/components/mcp/MCPToolsTab';
import MCPGatewayTab from '@/components/mcp/MCPGatewayTab';
import IntegrationTemplatesTab from '@/components/mcp/IntegrationTemplatesTab';
import IdentitySelector from '@/components/ai/IdentitySelector';
import { useToast } from '@/hooks/use-toast';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';


interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at: string | null;
  created_at: string;
  key_type?: 'public' | 'secret';
  allowed_form_ids?: string[];
  allowed_origins?: string[];
}

interface CreatedApiKey extends ApiKey {
  raw_key: string;
}


const PURCHASE_ORDER_IMPORT_SCOPE = 'pos:purchase-orders:import';
const availableScopes = ALL_AVAILABLE_SCOPES;

export function ApiKeysTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [keyTypeTab, setKeyTypeTab] = useState<'secret' | 'public'>('secret');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['calendar:read', 'calendar:write']);
  const [allowedOriginsText, setAllowedOriginsText] = useState('');
  const [allowedFormIdsText, setAllowedFormIdsText] = useState('');
  const [createActsAsUserIds, setCreateActsAsUserIds] = useState<string[]>([]);
  const [createAllowedSenderIds, setCreateAllowedSenderIds] = useState<string[] | null>(null);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [editName, setEditName] = useState('');
  const [editAllowedOriginsText, setEditAllowedOriginsText] = useState('');
  const [editAllowedFormIdsText, setEditAllowedFormIdsText] = useState('');

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['api-keys', keyTypeTab],
    queryFn: async () => {
      const res = await api.get(`/api-keys?keyType=${keyTypeTab}`);
      // Envelope difensivo: alcuni handler restituiscono { data: [...] },
      // altri l'array diretto — unwrap una sola volta.
      const d = res.data as { data?: ApiKey[] } | ApiKey[] | undefined;
      return ((Array.isArray(d) ? d : d?.data) ?? []) as ApiKey[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      scopes: string[];
      keyType: 'public' | 'secret';
      allowedOrigins?: string[];
      allowedFormIds?: string[];
      actsAsUserIds?: string[];
      allowedSenderIds?: string[] | null;
    }) => {
      const res = await api.post('/api-keys', data);
      // Envelope difensivo: { data: {...} } oppure oggetto diretto.
      const d = res.data as { data?: CreatedApiKey } & Partial<CreatedApiKey> | undefined;
      return (d?.data ?? d) as CreatedApiKey;
    },
    onSuccess: (data) => {
      setCreatedKey(data);
      setNewKeyName('');
      setSelectedScopes(['calendar:read', 'calendar:write']);
      setAllowedOriginsText('');
      setAllowedFormIdsText('');
      setCreateActsAsUserIds([]);
      setCreateAllowedSenderIds(null);
      setIsCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast({ title: 'API Key Created', description: 'Your new API key is ready. Copy it now!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create API key', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      name?: string;
      allowedOrigins?: string[];
      allowedFormIds?: string[];
    }) => {
      const { id, ...patch } = data;
      const res = await api.patch(`/api-keys/${id}`, patch);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      queryClient.invalidateQueries({ queryKey: ['public-api-keys'] });
      setEditingKey(null);
      toast({ title: 'API Key aggiornata', description: 'Le modifiche sono state salvate.' });
    },
    onError: (err: unknown) => {
      toast({ title: 'Errore', description: getApiErrorMessage(err) || 'Impossibile aggiornare la API key', variant: 'destructive' });
    },
  });

  const openEdit = (key: ApiKey) => {
    setEditingKey(key);
    setEditName(key.name || '');
    setEditAllowedOriginsText((key.allowed_origins || []).join(', '));
    setEditAllowedFormIdsText((key.allowed_form_ids || []).join(', '));
  };

  const handleEditSave = () => {
    if (!editingKey) return;
    const allowedOrigins = editAllowedOriginsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const allowedFormIds = editAllowedFormIdsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    updateMutation.mutate({
      id: editingKey.id,
      name: editName.trim() || editingKey.name,
      allowedOrigins,
      allowedFormIds,
    });
  };

  const revokeMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await api.delete(`/api-keys/${id}`);
      return res.data as { webhooks_disabled?: number } | undefined;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-configs'] });
      const msg = (data?.webhooks_disabled ?? 0) > 0
        ? `API Key revocata e ${data?.webhooks_disabled} webhook disabilitati automaticamente.`
        : 'La API key è stata revocata con successo.';
      toast({ title: 'API Key Revocata', description: msg });
    },
    onError: (err: unknown) => {
      toast({ title: 'Errore', description: getApiErrorMessage(err) || 'Impossibile revocare la API key', variant: 'destructive' });
    },
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: 'API Key copied to clipboard' });
  };

  const handleCreate = () => {
    if (!newKeyName.trim()) return;
    const allowedOrigins = allowedOriginsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const allowedFormIds = allowedFormIdsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    createMutation.mutate({
      name: newKeyName,
      scopes: selectedScopes,
      actsAsUserIds: createActsAsUserIds,
      allowedSenderIds: createAllowedSenderIds,
      keyType: keyTypeTab,
      allowedOrigins,
      allowedFormIds,
    });
  };

  const toggleScope = (scopeId: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId]
    );
  };

  return (
    <div className="space-y-6">
      {createdKey && (
        <Alert className="border-amber-500 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Save Your API Key</AlertTitle>
          <AlertDescription>
            <p className="mb-2 text-sm font-semibold text-destructive">
              This key will only be shown once. Copy and store it securely!
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                {createdKey.raw_key}
              </code>
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(createdKey.raw_key)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => setCreatedKey(null)}>
              I've saved it
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">API Keys</h3>
          <p className="text-sm text-muted-foreground">
            Le <strong>Secret Keys</strong> (sk_) servono per integrazioni server-to-server. Le <strong>Public Keys</strong> (pk_) servono per il widget di prenotazione embeddato.
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> {keyTypeTab === 'public' ? 'Crea Public Key' : 'Crea Secret Key'}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{keyTypeTab === 'public' ? 'Nuova Public Key (pk_live_)' : 'Nuova Secret Key (sk_live_)'}</DialogTitle>
              <DialogDescription>
                {keyTypeTab === 'public'
                  ? 'Le public key sono pensate per essere esposte nel widget di prenotazione. Limita gli Origins e i Form per ridurre l\'impatto di eventuali fughe.'
                  : 'Le secret key consentono l\'accesso server-to-server alle API. Conservale in modo sicuro.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Nome chiave</Label>
                <Input id="key-name" placeholder={keyTypeTab === 'public' ? 'es. Widget sito vetrina' : 'es. Integrazione CRM'} value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
              </div>

              <IdentitySelector
                value={{ acts_as_user_ids: createActsAsUserIds, allowed_sender_ids: createAllowedSenderIds }}
                canTenantWide={user?.role === 'admin' || user?.role === 'super_admin'}
                onChange={(next) => {
                  setCreateActsAsUserIds(next.acts_as_user_ids);
                  setCreateAllowedSenderIds(next.allowed_sender_ids);
                }}
              />

              {keyTypeTab === 'public' ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="allowed-origins">Origins consentiti</Label>
                    <Input
                      id="allowed-origins"
                      placeholder="https://www.miosito.it, https://booking.miosito.it"
                      value={allowedOriginsText}
                      onChange={(e) => setAllowedOriginsText(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Lista separata da virgole. Lasciare vuoto = nessuna restrizione (sconsigliato).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="allowed-forms">Form ID consentiti</Label>
                    <Input
                      id="allowed-forms"
                      placeholder="UUID separati da virgola — vuoto = tutti i form del tenant"
                      value={allowedFormIdsText}
                      onChange={(e) => setAllowedFormIdsText(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Suggerimento: usa la pagina "Form di Prenotazione" per generare la chiave già pre-associata.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Scopes</Label>
                  <div className="space-y-3 max-h-60 overflow-y-auto border rounded-md p-3">
                    {Object.entries(SCOPE_CATEGORIES).map(([catKey, catLabel]) => {
                      const catScopes = availableScopes.filter(s => s.category === catKey);
                      if (catScopes.length === 0) return null;
                      return (
                        <div key={catKey}>
                          <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{catLabel}</p>
                          {catScopes.map((scope) => (
                            <div key={scope.id} className="flex items-center space-x-2 ml-2">
                              <Checkbox id={scope.id} checked={selectedScopes.includes(scope.id)} onCheckedChange={() => toggleScope(scope.id)} />
                              <label htmlFor={scope.id} className="text-sm cursor-pointer">{scope.label}</label>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  {selectedScopes.includes(PURCHASE_ORDER_IMPORT_SCOPE) && (
                    <p role="note" className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200">
                      Scope amministrativo tenant-wide e di sola importazione: consente a un sistema server-to-server di creare
                      esclusivamente bozze canoniche di ordini fornitore. Non usare la chiave nel browser.
                    </p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Annulla</Button>
              <Button onClick={handleCreate} disabled={createMutation.isPending || !newKeyName.trim()}>
                {createMutation.isPending ? 'Creazione...' : 'Crea chiave'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={!!editingKey} onOpenChange={(o) => !o && setEditingKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Public Key</DialogTitle>
            <DialogDescription>
              Aggiorna nome, origins consentiti e form ID associati. La chiave completa rimane invariata.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-key-name">Nome chiave</Label>
              <Input id="edit-key-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-allowed-origins">Origins consentiti</Label>
              <Input
                id="edit-allowed-origins"
                placeholder="https://www.miosito.it, https://booking.miosito.it"
                value={editAllowedOriginsText}
                onChange={(e) => setEditAllowedOriginsText(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Lista separata da virgole o newline. Lasciare vuoto = nessuna restrizione (sconsigliato).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-allowed-forms">Form ID consentiti</Label>
              <Input
                id="edit-allowed-forms"
                placeholder="UUID separati da virgola — vuoto = tutti i form del tenant"
                value={editAllowedFormIdsText}
                onChange={(e) => setEditAllowedFormIdsText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingKey(null)}>Annulla</Button>
            <Button onClick={handleEditSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Salvataggio...' : 'Salva modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs value={keyTypeTab} onValueChange={(v) => setKeyTypeTab(v as 'public' | 'secret')}>
        <TabsList>
          <TabsTrigger value="secret">Secret Keys (sk_)</TabsTrigger>
          <TabsTrigger value="public">Public Keys (pk_)</TabsTrigger>
        </TabsList>
        <TabsContent value="secret" />
        <TabsContent value="public" />
      </Tabs>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Prefisso</TableHead>
              <TableHead>{keyTypeTab === 'public' ? 'Origins / Forms' : 'Scopes'}</TableHead>
              <TableHead>Ultimo uso</TableHead>
              <TableHead>Creata</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Caricamento...</TableCell></TableRow>
            ) : !apiKeys?.length ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nessuna chiave creata.</TableCell></TableRow>
            ) : (
              apiKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium">{key.name}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{key.prefix}...</code>
                  </TableCell>
                  <TableCell>
                    {keyTypeTab === 'public' ? (
                      <div className="space-y-1">
                        <div className="flex flex-wrap gap-1">
                          {(key.allowed_origins || []).length === 0 ? (
                            <Badge variant="outline" className="text-xs text-amber-600">Tutti gli origins</Badge>
                          ) : (
                            (key.allowed_origins || []).map((o) => (
                              <Badge key={o} variant="secondary" className="text-xs">{o}</Badge>
                            ))
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(key.allowed_form_ids || []).length === 0 ? (
                            <Badge variant="outline" className="text-xs">Tutti i form</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">{(key.allowed_form_ids || []).length} form</Badge>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.map((s) => (
                          <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {key.last_used_at ? format(new Date(key.last_used_at), 'MMM d, yyyy HH:mm') : 'Never'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(key.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(key.prefix)} title="Copy prefix">
                        <Copy className="h-4 w-4" />
                      </Button>
                      {keyTypeTab === 'public' && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(key)} title="Modifica">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => {
                        if (confirm('Revocare questa API key? Questa azione non può essere annullata.')) {
                          revokeMutation.mutate({ id: key.id });
                        }
                      }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

